import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Resource, Session } from '../shared/model/resource.model';
import { SaleItem } from '../shared/model/SaleItem';
import { VoucherDetails } from '../shared/model/voucher-details.model';
import { ConstantsService } from './constants.service';
import { APIResult, WebAPIResult } from './employee.service';
import { SessionContextService } from './session-context.service';
import * as dayjs from 'dayjs';
import { UntilDestroy } from '@ngneat/until-destroy';


export class AdjustCreditsDetail {
  ClientId: number;
  ReasonCode: number;
  CreditSystem: number;
  AdjustCreditsBy: number;
  NewCreditsActiveForDays: number;
  TerminalId: number;
  BranchId: number;
  EmployeeId: number;
  TimeOfTransaction: Date;
  Unlimited: boolean;
}

export class AdjustExpiryDetail {
  ClientId: number;
  ReasonCode: number;
  ClientCreditId: number;
  CreditSystem: number;
  CourseId: number;
  ExpiryDate: Date;
  NewExpiryDate: Date;
  TerminalId: number;
  BranchId: number;
  EmployeeId: number;
  TimeOfTransaction: Date;
  Unlimited: boolean;
}

export class dtoMakeSale {
  TransactionId: number;
  TimeOfTransaction: Date;
  ClientId: number;
  SoldByEmployeeId: number;
  BranchId: number;
  TerminalId: number;
  UseCredits: boolean;
  SaleItems: SaleItem[];
  AptId: number;
}

export class Transaction {
  TransactionHeader = -1;
  TotalSale = 0;
  TransactionDateTime: Date;

  public static CreateFrom(s: Transaction) {
    if (s) {
      const t: Transaction = new Transaction();
      t.TotalSale = s.TotalSale;
      t.TransactionDateTime = ConstantsService.MakeDateTimeGMT(s.TransactionDateTime);
      t.TransactionHeader = s.TransactionHeader;
      return t;
    }
    return null;
  }
}

export class VoidableTransactions {
  ClientId = -1;
  Transactions: Transaction[] = [];
  AllowEmailReceipts:boolean =false;
}

export class VoidSaleDetails {
  ClientId = -1;
  BranchId = -1;
  TerminalId = -1;
  ReasonCode = -1;
  TransactionIdList: number[] = [];
  EmailReceipt:boolean =false;
}

export class AmendableTransactions {
  ClientId = -1;
  Transactions: Transaction[] = [];
}

export class AmendableSale {
  TransactionHeader: number;
  TransactionDateTime: Date;
  AmountPaid: number;
  TenderType: number;
  TenderTypeDesc: number;
  Description: string;
}

@UntilDestroy({ checkProperties: true})
@Injectable({
  providedIn: 'root'
})
export class TransactionService implements OnDestroy {

  onAddSession: BehaviorSubject<Session> = new BehaviorSubject<Session>(null);
  onAddResource: BehaviorSubject<Resource> = new BehaviorSubject<Resource>(null);

  url = '/api/Transactions';
  constructor(
    private http: HttpClient,
    private session: SessionContextService) {
    console.log('Starting Transaction Service');
  }

  ngOnDestroy(): void {
    this.onAddSession?.unsubscribe();
    this.onAddResource?.unsubscribe();
  }

  InitMakeSaleData(ClientId: number, BranchId: number, TerminalId: number, EmployeeId: number, UseCredits: boolean, saleItems: SaleItem[], aptId: number): dtoMakeSale {
    const data: dtoMakeSale = new dtoMakeSale();
    // Ensures prices are correct
    saleItems.forEach(i => i.Price = eval(i.Price.toFixed(2)));
    data.TimeOfTransaction= dayjs().toDate()
    data.ClientId = ClientId;
    data.SoldByEmployeeId = EmployeeId;
    data.SaleItems = saleItems;
    data.BranchId = BranchId;
    data.TerminalId = TerminalId;
    data.UseCredits = UseCredits;
    data.AptId = aptId;
    return data;
  }

  public AddSession(session: Session) {
    if (session) {
      this.onAddSession.next(session);
    }
  }

  public AddResource(resource: Resource) {
    // if (resource) {
      this.onAddResource.next(resource);
    // }
  }

  public Reset() {
    this.onAddSession.next(null);
    this.onAddResource.next(null);
  }

  public CheckSale(ClientId: number, BranchId: number, TerminalId: number, UseCredits: boolean, saleItems: SaleItem[], aptId: number): Observable<SaleItem[]> {
    if (saleItems.length < 1) {
      return of(null);
    }
    const data: dtoMakeSale = this.InitMakeSaleData(ClientId, BranchId, TerminalId, -1, UseCredits, saleItems, aptId);    
    return this.http.post<SaleItem[]>(this.url + '/CheckSale', data).pipe(
      take(1),
      catchError((err) => {
        return throwError(err);
      }),
      map((m) => {
        return m;
      })
    );
  }

  public MakeSale(ClientId: number, BranchId: number, TerminalId: number, EmployeeId: number, saleItems: SaleItem[], aptId: number = 0): Observable<APIResult> {
    var UseCredits: boolean = true;
    const data: dtoMakeSale = this.InitMakeSaleData(ClientId, BranchId, TerminalId, EmployeeId, UseCredits, saleItems, aptId);    
    return this.http.post<WebAPIResult>(this.url + '/MakeSale', data).pipe(
      take(1),
      // tap(d => {
      //     console.log(d);
      // }),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return of(result);
      }),
      map((m) => {
        const result: APIResult = new APIResult();
        result.Success = m.Success;
        result.Msg = m.Msg;
        return result;
      })
    );
  }

  public GetAmendSaleTransactionList(ClientId: number, BranchId: number): Observable<AmendableTransactions> {
    const data = {
      ClientId: ClientId,
      BranchId: BranchId,
    };
    return this.http.post<AmendableTransactions>(this.url + '/GetAmendSaleTransactionList', data).pipe(
      take(1),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  public AmendSale(data: AmendableSale): Observable<any> {
    return this.http.post<APIResult>(this.url + '/AmendSale', data).pipe(
      take(1),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  public GetVoidSaleTransactionList(ClientId: number, BranchId: number): Observable<VoidableTransactions> {
    const data = {
      ClientId: ClientId,
      BranchId: BranchId,
    };
    return this.http.post<VoidableTransactions>(this.url + '/GetVoidSaleTransactionList', data).pipe(
      take(1),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  public VoidSale(data: VoidSaleDetails): Observable<any> {
    data.TerminalId = this.session.ctx.TerminalId;
    data.BranchId = this.session.ctx.BranchId;
    return this.http.post<APIResult>(this.url + '/VoidSale', data).pipe(
      take(1),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  public AdjustCredits(data: AdjustCreditsDetail): Observable<any> {
    data.TerminalId = this.session.ctx.TerminalId;
    data.BranchId = this.session.ctx.BranchId;
    data.EmployeeId = this.session.ctx.EmployeeId;
    data.TimeOfTransaction = dayjs().toDate();

    if (data.Unlimited) {
      data.AdjustCreditsBy = 0;
    }

    return this.http.post<APIResult>(this.url + '/AdjustCredits', data).pipe(
      take(1),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  public AdjustCourseExpiry(data: AdjustExpiryDetail): Observable<any> {
    const form: AdjustExpiryDetail = { ...data };
    form.TerminalId = this.session.ctx.TerminalId;
    form.BranchId = this.session.ctx.BranchId;
    form.EmployeeId = this.session.ctx.EmployeeId;
    form.TimeOfTransaction = dayjs().toDate();
    form.NewExpiryDate = ConstantsService.MakeDateTimeGMT(dayjs(data.NewExpiryDate).add(1, 'day').add(1, 'hour').toDate());

    return this.http.post<APIResult>(this.url + '/AdjustCourseExpiry', form).pipe(
      take(1),
      catchError((e) => {
        const result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  public ValidateNewVoucherCode(voucherCode: any): Observable<any> {    
    var data = {
      VoucherCode:voucherCode,      
    }
    return this.http.post<any>('/api/Transactions/ValidateNewVoucherCode', data).pipe(
      take(1),
      catchError((e) => {
        console.log('Validate new voucher error');
        console.log(e);
        return of(e);
      }),
      map((m) => {
        return m.Result;
      })
    );
  }

  GetVoucher(voucherCode: string):Observable<VoucherDetails>{
    var data = {
      VoucherCode: voucherCode,
    };
    return this.http.post<VoucherDetails>('/api/Transactions/GetVoucherDetails', data).pipe(
      take(1),
      catchError((e) => {
        console.log('Error getting Voucher Details', e);
        return of(null);
      }),
      map((m) => {
        return m;
      })
    );
  }
}
